import { FC, Fragment, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
// Types
import { AuditOperations } from 'app/store/Audit/Audit.types';
// Models
import IReport from 'app/models/Report';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Async
import { createAuditRecord } from 'app/store/Audit/Audit.async';
import { deleteReport, downloadReportFile } from 'app/store/CaseReports/CaseReports.async';
// Selectors
import { selectMyUser } from 'app/store/Users/Users.selectors';
import { selectLoading } from 'app/store/CaseReports/CaseReports.selectors';
// Mui
import { Theme, Paper, Box, Divider, Typography, Chip, CircularProgress } from '@mui/material';
// Icon
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
// Dialogs
import LEGACYReportFormDialog from 'app/dialogs/ReportFormDialog';
import ConfirmationDialog from 'app/dialogs/ConfirmationDialog';
// Components
import { IconButton } from 'app/components/Mui';
import { InfoBlock } from 'app/components/Utilities';
import Icon from 'app/components/Icon';
// Hooks
import useCaseStatus from 'app/hooks/useCaseStatus';
import useMenu from 'app/hooks/useMenu';
import useToggle from 'app/hooks/useToggle';
import useLongDateTime from 'app/hooks/useLongDateTime';
import useAccountStatus from 'app/hooks/useAccountStatus';
// ToDO: Sevice
import MixpanelTracks from 'app/types/MixpanelTracks';
import Mixpanel from 'app/services/Mixpanel.service';
// 
import ReportFormDialog from './ReportFormDialog';
import ReportUploadDialog from './ReportUploadDialog';
import ReportShareDialog from './ReportShareDialog';
import { statusThemeColor } from 'app/utilities/Utilities';
import ReportStatuses from 'app/types/ReportStatuses';

type Props = {
  report: IReport;
}

const ReportsListItem:FC<Props> = ({
  // Props
  report
}) => {
  const { t } = useTranslation();

  const { caseId } = useParams<{ caseId:string }>();
  const history = useHistory();

  const { isArchived } = useCaseStatus();
  const { isSuspended } = useAccountStatus();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const myUser = useAppSelector(selectMyUser);
  const loading = useAppSelector(selectLoading);

  const { Menu, MenuItem, openMenu } = useMenu();
  const { open, toggle } = useToggle();
  const { open:openLegacyFormDialog, toggle:toggleLegacyFormDialog } = useToggle();
  const { open:openUploadDialog, toggle:toggleUploadDialog } = useToggle();
  const { open:openShareDialog, toggle:toggleShareDialog } = useToggle();
  const { open:openConfirmationDialog, toggle:toggleConfirmationDialog } = useToggle();

  const [ stateLoading, setStateLoading ] = useState(false);

  const longCreatedOn = useLongDateTime({
    dateValue: report.createdOn,
    displayTime: true,
    local: true
  });
  const longDownloadedOn = useLongDateTime({
    dateValue: report.downloadedOn,
    displayTime: true,
    local: true
  });
  const longSharedOn = useLongDateTime({
    dateValue: report.sharedOn,
    displayTime: true,
    local: true
  });

  const handleOpenReportPreview = () => {
    history.push(`/admin/cases/${caseId}/reports?reportId=${report.id}`);
  }

  const handleReportDownloadFile = (format:any) => async () => {
    setStateLoading(true);
    try {
      await dispatch(downloadReportFile({ reportId: report.id, params:{ format }}));
    } catch(error){} finally {
      setStateLoading(false);
    }
  }

  const handleConfirm = async () => {
    const insuranceCaseId = Number(caseId);
    try {
      await dispatch(deleteReport(report.id)).unwrap();

      Mixpanel.track(MixpanelTracks.CaseReportRemoved, { caseId: insuranceCaseId });

      if ( report.shared ){
        dispatch(createAuditRecord({
          operation: AuditOperations.ReportDeleted,
          data: {
            caseId: insuranceCaseId,
            reportName: report.name,
            userName: myUser?.name || '',
            clientUserEmails: report.notificationRecipients.map((recipient:any) => recipient.email)
          }
        }));
      } else {
        dispatch(createAuditRecord({
          operation: AuditOperations.ReportDeleted,
          data: {
            caseId: insuranceCaseId,
            reportName: report.name,
            userName: myUser?.name || ''
          }
        }));
      }
    } catch(error){}
  }

  const isDisabled = isArchived || isSuspended;

  const menuItems = [
    {
      key: `Create a new version ${report.id}`,
      onClick: toggle,
      disabled: isSuspended,
      label: t('pages.reports.createANewVersion'),
      show: report.status === ReportStatuses.Ready
    },
    {
      key: `Edit report ${report.id}`,
      onClick: toggleLegacyFormDialog,
      disabled: isDisabled,
      label: t('pages.reports.renameButton'),
      show: !report.shared && report.status === ReportStatuses.Ready
    },
    {
      key: `Share report ${report.id}`,
      onClick: toggleShareDialog,
      disabled: isDisabled,
      label: t('pages.reports.shareButton'),
      show: !report.shared && report.status === ReportStatuses.Ready
    },
    {
      key: `Preview report ${report.id}`,
      onClick: handleOpenReportPreview,
      label: t('pages.reports.previewButton'),
      show: report.status === ReportStatuses.Ready
    },
    { key: 'divider-1', show: report.status === ReportStatuses.Ready },
    {
      key: `Download report ${report.id} pdf`,
      onClick: handleReportDownloadFile('pdf'),
      disabled: stateLoading,
      label: t('pages.reports.downloadPdfButton'),
      show: report.status === ReportStatuses.Ready
    },
    {
      key: `Download report ${report.id} word`,
      onClick: handleReportDownloadFile('word'),
      disabled: stateLoading,
      label: t('pages.reports.downloadWordButton'),
      show: report.status === ReportStatuses.Ready
    },
    {
      key: `Upload file ${report.id}`,
      onClick: toggleUploadDialog,
      disabled: isDisabled,
      label: t('buttons.upload'),
      show: !report.shared && report.status === ReportStatuses.Ready
    },
    { key: 'divider-2', show: report.status === ReportStatuses.Ready },
    {
      key: `Delete report ${report.id}`,
      onClick: toggleConfirmationDialog,
      disabled: isArchived,
      label: t('buttons.delete'),
      sx: { color: (theme:Theme) => theme.palette.error.main  },
      show: report.status === ReportStatuses.Ready || report.status === ReportStatuses.Failed
    }
  ];

  const visibleMenuItems = menuItems.filter(item => item.show);

  return (
    <Fragment>
      <Paper sx={{ display: 'flex', p: 4, '& + &': { mt: 4 } }}>
        <Box sx={{ flexGrow: 1, pr: 2 }}>
          {report.status !== ReportStatuses.Ready ? (
            <Typography
              sx={{
                display: 'block',
                color: (theme:Theme) => statusThemeColor(report.status, theme),
                fontWeight: 700,
                textTransform: 'capitalize'
              }}
              variant="caption"
            >{report.status}</Typography>
          ) : null}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ flexGrow: 1 }} variant="subtitle1">{report.name}</Typography>
            {report.sharedOn ? (
              <Icon size="small" icon="shared" label="Shared" />
            ) : null}
          </Box>
          {report.notificationRecipients && report.notificationRecipients.length ? (
            <InfoBlock
              label={t('labels.sharedTo')}
              value={
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {report.notificationRecipients.map((recipient:any, index:number) => (
                    <Chip
                      key={`report-item-${report.id}-recipient-${index}`}
                      sx={{ borderRadius: 1 }}
                      label={recipient.email}
                      size="small"
                    />
                  ))}
                </Box>
              }
            />
          ) : null}
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', gap: 4, mr: -12 }}>
            {longCreatedOn ? (
              <InfoBlock label={t('labels.createdAt')} value={longCreatedOn} />
            ) : null}
            {longDownloadedOn ? (
              <InfoBlock label={t('labels.downloadedAt')} value={longDownloadedOn} />
            ) : null}
            {longSharedOn ? (
              <InfoBlock label={t('labels.sharedAt')} value={longSharedOn} />
            ) : null}
          </Box>
        </Box>
        {visibleMenuItems.length ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: -2, mr: -2 }}>
            <IconButton
              name={`Report dropdown ${report.id}`}
              aria-label="Report item dropdown button"
              onClick={openMenu}
            ><MoreVertIcon /></IconButton>
            {stateLoading ? (
              <CircularProgress size={24} thickness={4} />
            ) : null}
            <Menu>
              {visibleMenuItems.map(item => item.key.startsWith('divider') ? (
                <Divider key={item.key} />
              ) : (
                <MenuItem
                  key={item.key}
                  name={item.key}
                  onClick={item.onClick}
                  disabled={item.disabled}
                  sx={item.sx}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        ) : null}
      </Paper>
      {open ? (
        <ReportFormDialog
          open={open}
          onClose={toggle}
          report={report}
        />
      ) : null}
      {/* Dialogs */}
      {openLegacyFormDialog ? (
        <LEGACYReportFormDialog
          open={openLegacyFormDialog}
          onClose={toggleLegacyFormDialog}
          report={report}
        />
      ) : null}
      {openUploadDialog ? (
        <ReportUploadDialog
          open={openUploadDialog}
          onClose={toggleUploadDialog}
          reportId={report.id}
        />
      ) : null}
      {openShareDialog ? (
        <ReportShareDialog
          open={openShareDialog}
          onClose={toggleShareDialog}
          reportId={report.id}
          reportName={report.name}
        />
      ) : null}
      {openConfirmationDialog ? (
        <ConfirmationDialog
          open={openConfirmationDialog}
          onClose={toggleConfirmationDialog}
          loading={loading}
          title={t('dialogs.confirmation.deleteTitle', { dataType: t('labels.report') })}
          content={(
            <Trans
              t={t} i18nKey="dialogs.confirmation.deleteConfirmationWithName"
              components={{ strong: <strong /> }}
              values={{ dataType: t('labels.report'), dataName: report.name }}
            />
          )}
          onConfirm={handleConfirm}
        />
      ) : null}
    </Fragment>
  )
}

export default ReportsListItem;
